// Map performance bench — renders the real MapView on a synthetic board and
// plays a scripted turn sequence while sampling UI-thread frame times.
//
//   warcarp://preview/map-bench?map=arctic&scenario=selection&reps=12
//   warcarp://preview/map-bench?map=arctic&scenario=all
//   warcarp://preview/map-bench?map=arctic&hold=preview   (or hold=target)
//
// `hold` skips the timeline and freezes one selection state (source plus
// target glows, or source plus picked target) for screenshot comparisons
// between builds; it logs `MAP_BENCH_HOLD` once the state is applied.
// Each finished scenario logs one line, `MAP_BENCH_RESULT {json}`, which
// scripts/map-bench.mjs reads from logcat (Android) or the device log (iOS).
// Measure on a Release build on a real device: Debug and emulators distort
// frame costs. See docs/map-perf.md → "Map bench".
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Text, View } from 'react-native'
import { Stack, useLocalSearchParams } from 'expo-router'
import { useFrameCallback, useSharedValue } from 'react-native-reanimated'
import type { GameEvent, MapDef } from '@warcarp/api-types'
import { MapView } from '../../../components/map/MapView'
import { attackPairs, BENCH_SCENARIOS, benchTimeline, buildBenchState, frameStats, type BenchScenario } from '../../../lib/mapBench'
import type { HydratedGameState } from '../../../lib/gameState'

const MAPS: Record<string, () => MapDef> = {
  arctic: () => require('../../../assets/maps/arctic.json'),
  carpathia: () => require('../../../assets/maps/carpathia.json'),
  minnesota: () => require('../../../assets/maps/minnesota.json'),
  'world-classic': () => require('../../../assets/maps/world-classic.json'),
  castles: () => require('../../../assets/maps/castles.json'),
}

export default function MapBenchScreen() {
  const params = useLocalSearchParams<{ map?: string; scenario?: string; reps?: string; hold?: string }>()
  const mapId = params.map && MAPS[params.map] ? params.map : 'arctic'
  const reps = Number(params.reps ?? 10)
  const queue = useMemo<BenchScenario[]>(() => params.scenario && params.scenario !== 'all' && BENCH_SCENARIOS.includes(params.scenario as BenchScenario)
    ? [params.scenario as BenchScenario] : BENCH_SCENARIOS, [params.scenario])
  const map = useMemo(() => (MAPS[mapId] ?? MAPS.arctic!)(), [mapId])

  const [state, setState] = useState<HydratedGameState>(() => buildBenchState(map))
  const [selection, setSelection] = useState<{ from?: string; to?: string; targets: string[] }>({ targets: [] })
  const [status, setStatus] = useState('starting…')
  const [results, setResults] = useState<string[]>([])
  const joinedAt = useRef(Date.now()).current

  // UI-thread frame deltas for the running scenario.
  const recording = useSharedValue(false)
  const deltas = useSharedValue<number[]>([])
  useFrameCallback((frame) => {
    'worklet'
    if (!recording.value || frame.timeSincePreviousFrame == null) return
    const delta = frame.timeSincePreviousFrame
    deltas.modify((list) => { 'worklet'; list.push(delta); return list })
  })

  useEffect(() => {
    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []
    const wait = (ms: number) => new Promise<void>((resolve) => { timers.push(setTimeout(resolve, ms)) })
    void (async () => {
      await wait(1500) // first paint and map setup settle
      if (params.hold === 'preview' || params.hold === 'target') {
        const pair = attackPairs(state)[0]
        if (!pair) return
        setSelection({ from: pair.from, targets: pair.targets, to: params.hold === 'target' ? pair.targets[0] : undefined })
        await wait(1500)
        setStatus(`${mapId} · hold ${params.hold}`)
        console.log(`MAP_BENCH_HOLD ${JSON.stringify({ map: mapId, hold: params.hold })}`)
        return
      }
      for (const scenario of queue) {
        if (cancelled) return
        const fresh = buildBenchState(map)
        setState(fresh)
        setSelection({ targets: [] })
        await wait(800)
        const startTs = Date.now()
        const steps = benchTimeline(scenario, attackPairs(fresh), reps, startTs)
        setStatus(`${mapId} · ${scenario} · running`)
        deltas.value = []
        recording.value = true
        // The collector resets Android's frame stats (dumpsys gfxinfo) on this line.
        console.log(`MAP_BENCH_START ${JSON.stringify({ map: mapId, scenario })}`)
        const started = Date.now()
        for (const step of steps) {
          await wait(Math.max(0, step.at - (Date.now() - started)))
          if (cancelled) return
          const action = step.action
          if (action.type === 'select') setSelection({ from: action.from, targets: action.targets })
          else if (action.type === 'target') setSelection((s) => ({ ...s, to: action.to }))
          else if (action.type === 'clear') setSelection({ targets: [] })
          else setState((s) => applyEvents(s, action.events, action.ownerChange))
        }
        recording.value = false
        await wait(100) // let the last frame's write land before reading it on JS
        const list = [...deltas.value]
        const stats = frameStats(list)
        const line = { map: mapId, scenario, reps, durationMs: Date.now() - started, ...stats }
        console.log(`MAP_BENCH_RESULT ${JSON.stringify(line)}`)
        setResults((r) => [...r, `${scenario}: mean ${stats.meanMs}ms p95 ${stats.p95Ms}ms >33ms ${stats.over33}/${stats.frames} worst ${stats.worstMs}ms`])
      }
      console.log(`MAP_BENCH_DONE ${JSON.stringify({ map: mapId })}`)
      setStatus(`${mapId} · done`)
    })()
    return () => { cancelled = true; timers.forEach(clearTimeout) }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `state` is read once for a hold
  }, [map, mapId, queue, reps, deltas, recording, params.hold])

  const previewTargetIds = selection.from && !selection.to ? selection.targets : []
  return (
    <View style={{ flex: 1, backgroundColor: '#f1e8cf' }}>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={{ flex: 1 }}>
        <MapView
          state={state}
          viewerSlot={1}
          selectedTerritoryId={selection.from}
          secondaryTerritoryId={selection.to}
          previewTargetIds={previewTargetIds}
          validTargetIds={selection.targets}
          selectedFillMode="lighten"
          events={state.events}
          viewerJoinedAt={joinedAt}
          trimMargins
        />
      </View>
      <View style={{ padding: 8 }} testID="map-bench-status">
        <Text style={{ fontWeight: '700' }}>{status}</Text>
        {results.map((line) => <Text key={line} style={{ fontSize: 11 }}>{line}</Text>)}
      </View>
    </View>
  )
}

function applyEvents(state: HydratedGameState, events: GameEvent[], ownerChange?: { territoryId: string; ownerId: string; armies: number }): HydratedGameState {
  const territories = ownerChange
    ? state.map.territories.map((t) => (t.id === ownerChange.territoryId ? { ...t, ownerId: ownerChange.ownerId, armies: ownerChange.armies } : t))
    : state.map.territories
  return { ...state, map: { ...state.map, territories }, events: [...(state.events ?? []), ...events] }
}

